import {FC, useState} from 'react'
import {gql} from '@apollo/client'
import JbcBankSearchModal from 'jbc-front/components/BankSearchModal'
import {useLazyQuery} from 'hooks/graphql'

const SEARCH_BANKS = gql`
  query searchBanks($page: Int, $q: String) {
    banks(page: $page, q: $q) {
      totalCount
      list {
        id
        name
        bankCode
      }
    }
  }
`

interface SearchBanksQueryVariables {
  page?: number
  q?: string
}

interface SearchBanksQueryResult {
  banks: {
    totalCount: number
    list: Bank[]
  }
}

export interface Bank {
  id: string
  name: string
  bankCode: string
}

const SEARCH_BANK_BRANCHES = gql`
  query searchBankBranches($bankId: ID!, $page: Int, $q: String) {
    bank(id: $bankId) {
      bankBranches(page: $page, q: $q) {
        totalCount
        list {
          id
          name
          branchCode
        }
      }
    }
  }
`

interface SearchBankBranchesQueryVariables {
  bankId: string
  page?: number
  q?: string
}

interface SearchBankBranchesQueryResult {
  bank: {
    bankBranches: {
      totalCount: number
      list: BankBranch[]
    }
  }
}

export interface BankBranch {
  id: string
  name: string
  branchCode: string
}

interface BankSearchModalProps {
  isOpen: boolean
  hideModal: () => void
  onSelect: (bank: Bank, branch: BankBranch) => void
}

export const BankSearchModal: FC<BankSearchModalProps> = ({isOpen, hideModal, onSelect}) => {
  const [searchBanks, searchBanksResult] = useLazyQuery<SearchBanksQueryResult, SearchBanksQueryVariables>(
    SEARCH_BANKS,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  )
  const [searchBankBranches, searchBankBranchesResult] = useLazyQuery<
    SearchBankBranchesQueryResult,
    SearchBankBranchesQueryVariables
  >(SEARCH_BANK_BRANCHES, {fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true})

  const [currentPage, setCurrentPage] = useState(0)
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null)

  const totalCount = selectedBank
    ? searchBankBranchesResult.data?.bank?.bankBranches?.totalCount
    : searchBanksResult.data?.banks?.totalCount

  const reset = () => {
    setCurrentPage(0)
    setSelectedBank(null)
  }

  return (
    <JbcBankSearchModal
      isOpen={isOpen}
      hideModal={hideModal}
      selectedBank={selectedBank}
      banks={searchBanksResult.data?.banks?.list || []}
      branches={searchBankBranchesResult.data?.bank?.bankBranches?.list || []}
      count={totalCount}
      currentPage={currentPage}
      handleBankSearch={(query, page) => {
        setCurrentPage(page || 0)
        searchBanks({variables: {q: query, page}})
      }}
      handleBranchSearch={(bank, query, page) => {
        setCurrentPage(page || 0)
        searchBankBranches({variables: {bankId: bank.id, q: query, page}})
      }}
      onBankSelect={(bank) => {
        setSelectedBank(bank)
        searchBankBranches({variables: {bankId: bank.id, q: ''}})
      }}
      onReset={() => {
        reset()
        searchBanks({variables: {q: ''}})
      }}
      onSelect={(bank, branch) => {
        onSelect(bank, branch)
        reset()
        hideModal()
      }}
      onHide={() => {
        reset()
      }}
    />
  )
}
